import React from "react";
import colors from "../../../../theme/colors";
import {
  booleanOptions,
  DataTypeEnum,
  DomainEnum,
  PropertyResponse,
  Property,
  PropertyType,
} from "store/reducers/products/properties/pl-properties.types";
import { PropertiesDropdown } from "./PropertiesDropdown";
import { getEnumPropertiesOptions } from "components/Table/helper";
import {
  Ruleset,
  Tag,
} from "store/reducers/products/parameters/pl-parameters.types";
import NameCell from "./NameCell";
import { SearchableDropdown, ToolTip, TypeConstrainedCell } from "components";
import { editPropertyPvaCell } from "store/reducers/products/properties/pl-properties.actions";
import { determineDataTypeOptions } from "../../pages/ProductDetail/helpers";
import { editSubPropertyPvaCell } from "../../../../store/reducers/products/subproperties/pl-subproperties.actions";
import { api } from "../../../../api/api";
import { RuleTable } from "types/table";

const dataCellStyles = {
  minHeight: "40px",
  minWidth: "276px",
  color: colors.muttedHeadlines,
  fontSize: "14px",
};

const headerCellStyles = {
  backgroundColor: colors.tables.columnsAndRowsTitles,
  color: colors.muttedHeadlines,
  minWidth: "180px",
  minHeight: "40px",
};

export const propertyHasValues = (property: PropertyResponse) => {
  return property.propertyValues.some(
    (row) => row.valueAttributeCells[0].value !== ""
  );
};

export interface EnrichedRuleTable {
  id: string;
  propertyIds: string[];
  rulesetIds: string[];
  lookUpFieldIndex: number;
  tableType: string;
  properties: {
    id: string;
    name: string;
    parentId?: string;
  }[];
  rulesets: Ruleset[];
}

interface RelatedPropertiesResponse {
  ruleTables: EnrichedRuleTable[];
  [key: string]: any;
}

/**
 * Retrieves related properties and their rule tables for a given property.
 *
 * @param productLineId - The ID of the product line
 * @param propertyId - The ID of the property to find relations for
 * @returns Promise resolving to the rule tables data with enriched property and ruleset information
 */
export const getRelatedProperties = async (
  productLineId: string,
  propertyId: string
): Promise<RelatedPropertiesResponse> => {
  try {
    // Fetch all required data in parallel for better performance
    const [ruleTablesResponse, rulesetsResponse, propertiesResponse] =
      await Promise.all([
        // Get rule tables containing the specified property
        api.getRuleTablesLight(
          productLineId,
          "0", // Fetch all tables (consider pagination for large datasets)
          0, // Start from the first page
          undefined,
          undefined,
          [propertyId]
        ),

        // Get all rulesets for this product line
        api.getProductLineRulesets(productLineId),

        // Get lightweight property data including subproperties
        api.getPropertiesLight(productLineId, {
          items: "0", // Fetch all properties
          includeSubProperties: true,
          includePVAProperties: true,
        }),
      ]);

    // Extract data from responses
    const ruleTables: RuleTable[] = ruleTablesResponse.data.ruleTables || [];
    const rulesets: Ruleset[] = rulesetsResponse?.data || [];
    const properties: Property[] = propertiesResponse?.data?.properties || [];

    // Enrich rule tables with property and ruleset information
    const enrichedRuleTables: EnrichedRuleTable[] = ruleTables.map((table) => {
      // Map property IDs to property objects with names
      const tableProperties = table.propertyIds.map((id: string) => {
        const property = properties.find((p) => p.id === id);
        return {
          id,
          name: property?.name || `Unknown (${id})`,
          parentId: property?.parentId || "",
        };
      });

      // Map ruleset IDs to ruleset objects
      const tableRulesets: Ruleset[] = table.rulesetIds.map(
        (rulesetId) =>
          rulesets.find((r) => r.id === rulesetId) ||
          ({
            id: rulesetId,
            name: `Unknown (${rulesetId})`,
          } as Ruleset)
      );

      // Return enriched table with properties and rulesets
      return {
        ...table,
        properties: tableProperties,
        rulesets: tableRulesets,
      };
    });

    // Return the data in the expected format
    return {
      ...ruleTablesResponse.data,
      ruleTables: enrichedRuleTables,
    };
  } catch (error) {
    console.error("Error fetching related properties:", error);
    // Return empty data structure on error
    return { ruleTables: [] };
  }
};

/**
 * Generates a Graphviz representation of tables with properties.
 * @param tables - The array of tables.
 * @param centerPropertyName - The name of the center property.
 * @param includeSubProperty - A flag indicating whether to include sub-properties.
 * @returns The Graphviz representation as a string.
 */
export const generateGraphviz = (
  tables,
  centerPropertyName,
  includeSubProperty
): string => {
  let dotContent = `digraph "" {overlap=false;splines=true;`;
  const props = new Set<string>();
  tables = tables.sort((a, b) => {
    if (a.tableType > b.tableType) {
      return -1;
    }
    if (a.tableType < b.tableType) {
      return 1;
    }
    return 0;
  });

  const sanitizeUrl = (url: string): string => {
    // Remove any characters that might cause issues in graphviz
    return encodeURIComponent(url)
      .replace(/[()]/g, "")
      .replace(/[<>"']/g, "");
  };
  const addEdge = (from, to, options = "") => {
    const edge = `  "${from}" -> "${to}"${options};`;
    if (!dotContent.includes(edge)) {
      dotContent += edge;
    }
  };

  const addProperties = (properties) => {
    if (properties) {
      properties.forEach((prop) => {
        props.add(prop.name);
      });
    }
  };

  tables.forEach((table) => {
    if (table.tableType === "LOOKUP" || table.tableType === "ATTR_LOOKUP") {
      const { properties = [], lookUpFieldIndex } = table;
      const dependantPropertiesName = properties
        .filter((_, index) => index >= lookUpFieldIndex)
        .filter((prop) => {
          return (
            includeSubProperty || prop.name.includes("::") || !prop.parentId
          );
        })
        .map((prop) => prop.name);

      const affectedPropertiesName = properties
        .filter((_, index) => index < lookUpFieldIndex)
        .filter(
          (prop) =>
            includeSubProperty || prop.name.includes("::") || !prop.parentId
        )
        .map((prop) => prop.name);

      addProperties(properties);
      const isAffected = affectedPropertiesName.includes(centerPropertyName);
      if (isAffected || dependantPropertiesName.includes(centerPropertyName)) {
        const color = isAffected
          ? "red"
          : table.tableType === "ATTR_LOOKUP"
          ? "pink"
          : "blue";
        const direction = isAffected ? "forward" : "back";

        if (isAffected) {
          dependantPropertiesName.forEach((propName) => {
            addEdge(
              propName,
              centerPropertyName,
              `[dir="${direction}" color="${color}" id="${propName},${table.tableType},${direction}"]` //URL="rules-tables/${sanitizeUrl(
              // table.id
              // )}" target=_blank
            );
          });
        } else {
          affectedPropertiesName.forEach((propName) => {
            addEdge(
              centerPropertyName,
              propName,
              `[color="${color}" id="${propName},${table.tableType},${direction}"]`
            );
          });
        }
      }
    } else if (table.tableType === "INCLUDE" || table.tableType === "EXCLUDE") {
      const color = table.tableType === "INCLUDE" ? "forestgreen" : "orange";
      const includePropertiesName = (table.properties || [])
        .filter((prop) => prop.name !== centerPropertyName)
        .filter(
          (prop) =>
            includeSubProperty || !prop.parentId || prop.name.includes("::")
        )
        .map((prop) => prop.name);

      includePropertiesName.forEach((propName) => {
        addEdge(
          centerPropertyName,
          propName,
          `[dir="both" color="${color}" id="${propName},${table.tableType},both"]` //URL="rules-tables/${sanitizeUrl(
          //   table.id
          // )}" target=_blank]
        );
      });

      addProperties(table.properties);
    }
  });
  //Center property emphasis
  dotContent += `  "${centerPropertyName}" [style=filled, fillcolor=yellow];`;
  props.forEach((prop) => {
    if (prop !== centerPropertyName && dotContent.includes(`"${prop}"`)) {
      const parentProp = prop.includes("::")
        ? prop.split("::")[0]
        : prop.includes(".")
        ? prop.split(".")[0]
        : prop;
      dotContent += `  "${prop}" [URL="properties?name=${sanitizeUrl(
        parentProp
      )}" target="_blank"];`;
    }
  });
  dotContent += "}";
  return dotContent;
};

// export const propertyHasFloatValues = property;

export const GetTableData = (
  propertyData: PropertyResponse,
  propertyType: PropertyType,
  ruleSetsOptions: Ruleset[] = [],
  tagsOptions: Tag[] = []
) => {
  const { ruleSets = [], tags = [] } = propertyData;
  const hasValues = propertyHasValues(propertyData);
  const dataTypeOptions = getEnumPropertiesOptions(
    determineDataTypeOptions(propertyData.domain, hasValues)
  );

  return {
    header: [],
    rows: [
      [
        {
          content: "NAME",
          style: headerCellStyles,
          id: "nameTitleColumn",
        },
        {
          content: (
            <NameCell
              propertyData={propertyData}
              id={"UniqueNameCellUUID"}
              type={propertyType}
            />
          ),
          id: "nameEditCell",
        },
      ],
      [
        {
          content: "DATATYPE",
          style: headerCellStyles,
          id: "dataTypeTitleColumn",
        },
        {
          content: (
            <ToolTip
              text={"remove values to enable changing the data type"}
              disabled={!dataTypeOptions.length}
              width={"100%"}
            >
              <PropertiesDropdown
                options={dataTypeOptions}
                currentValue={DataTypeEnum[propertyData.dataType]}
                multiselect={false}
                propertyData={propertyData}
                propertyParameterType={"dataType"}
                type={propertyType}
              />
            </ToolTip>
          ),
          style: dataCellStyles,
          id: "dropdownDataTypeCellUUID",
        },
      ],
      [
        {
          content: "RULESETS",
          style: headerCellStyles,
          id: "rulesetTitleCellUUID",
        },
        {
          content: (
            <PropertiesDropdown
              propertyData={propertyData}
              propertyParameterType={"rulesetids"}
              options={ruleSetsOptions}
              currentValue={ruleSets}
              placeholder="rulesets"
              multiselect={true}
              getOptionLabel={(option) => option.name}
              getOptionKey={(option) => option.id}
              type={propertyType}
            />
          ),
          style: dataCellStyles,
          id: "RulesetsUUIDDropdown",
        },
      ],
      [
        {
          content: "TAGS",
          style: headerCellStyles,
          id: "TagsUUIDTile",
        },
        {
          content: (
            <PropertiesDropdown
              propertyData={propertyData}
              propertyParameterType={"tagids"}
              options={tagsOptions}
              currentValue={tags}
              placeholder="tags"
              multiselect={true}
              getOptionLabel={(option) => option.name}
              getOptionKey={(option) => option.id}
              type={propertyType}
            />
          ),
          id: "SelectTagsUUIDCell",
          style: dataCellStyles,
        },
      ],
      [
        {
          content: "DOMAIN",
          style: headerCellStyles,
          id: "DomainTitleUUID",
        },
        {
          content: (
            <PropertiesDropdown
              options={getEnumPropertiesOptions(DomainEnum)}
              currentValue={DomainEnum[propertyData.domain]}
              multiselect={false}
              propertyData={propertyData}
              propertyParameterType={"domain"}
              type={propertyType}
            />
          ),
          style: dataCellStyles,
          id: "domainSelectCellUUID",
        },
      ],
      propertyData.domain === DomainEnum.DISCRETE && [
        {
          content: "AUTO-SELECTION",
          style: headerCellStyles,
          id: "AutoSelectTitleUUID",
        },
        {
          content: (
            <PropertiesDropdown
              options={booleanOptions}
              currentValue={propertyData?.autoSelection ?? false}
              multiselect={false}
              propertyData={propertyData}
              propertyParameterType={"autoSelection"}
              type={propertyType}
            />
          ),
          style: dataCellStyles,
          id: "domainSelectCellUUID",
        },
      ],
      propertyData.ownerTool !== "" && [
        {
          content: "OWNER TOOL",
          style: headerCellStyles,
          id: "OwnerToolUUID",
        },
        {
          content: propertyData?.ownerTool ?? "empty",
          style: dataCellStyles,
          id: "ownerToolCellUUID",
        },
      ],
    ],
  };
};

const customHeaderCellsStyles = { ...headerCellStyles, fontWeight: 500 };

export const getPvaTableData = (
  propertyData: any,
  dispatch: any,
  formattedRulesets: any,
  propertyType: PropertyType,
  parentId = ""
) => {
  const {
    propertyValueAttributes: activePvas,
    propertyValues,
    dataType: propertyDataType,
  } = propertyData;

  if (!activePvas) return;
  return {
    header: activePvas.map((pva) => ({
      content: pva.name,
      style: customHeaderCellsStyles,
      id: "VALUEtitleCellUUID" + pva.id,
    })),
    rows: propertyValues.map((row) => {
      const { valueAttributeCells } = row;

      return {
        content: valueAttributeCells.map((cell) => {
          const { value, propertyValueAttribute } = cell;
          const { dataType, id, name } = propertyValueAttribute;
          const uniqueId = id + row.id;

          if (name === "RULESET") {
            let formattedValues = [];
            try {
              formattedValues = JSON.parse(value);
            } catch (e) {
              formattedValues = [];
            }

            // MUI autocomplete assumes that value and options objects have the same structure.
            // So values are mapped to their corresponding ruleset options.
            const rulesetsByIds = Object.fromEntries(
              formattedRulesets.map((ruleset) => [ruleset.value, ruleset])
            );

            // Filtering out values not in the parent dictionary means that when a ruleset is
            // removed from a PVA parent, it will not be displayed in a PVA row's ruleset list. But
            // the PVA will still have the ruleset in its list and it will show up again if that
            // ruleset is added back to the PVA parent. If this is not done, there will be an error
            // since values may not be present in the options dictionary. If this is an issue something
            // such as cascading ruleset change similar to cascading rename could be added to
            // ensure values are present in options.
            const valuesForDropdown = formattedValues
              .filter((value) => value in rulesetsByIds)
              .map((value) => rulesetsByIds[value]);

            return {
              content: (
                <SearchableDropdown
                  multiselect
                  searchable
                  placeholder="rulesets"
                  options={formattedRulesets}
                  value={valuesForDropdown}
                  handleValueChange={(e) => {
                    propertyType === "main"
                      ? dispatch(
                          editPropertyPvaCell(
                            {
                              propertyId: propertyData.id,
                              propertyValueId: row.id,
                              propertyValueAttributeId: id,
                            },
                            // backend doesn't accept anything but strings
                            JSON.stringify(e).replace('\\"', "'"),
                            propertyData
                          )
                        )
                      : dispatch(
                          editSubPropertyPvaCell({
                            parentId: parentId,
                            propertyId: propertyData.id,
                            propertyValueAttributeId: id,
                            propertyValueId: row.id,
                            newValue: JSON.stringify(e).replace('\\"', "'"),
                            property: propertyData,
                          })
                        );
                  }}
                  id={uniqueId}
                  name="pvaRulesetSelect"
                  noShadow
                  noHoverBorder
                  backgroundColor={colors.tables.hoveredCell}
                />
              ),
              id: uniqueId,
            };
          }

          return {
            content: (
              <TypeConstrainedCell
                dataType={name === "Value" ? propertyDataType : dataType}
                type="editCell"
                handleSubmit={(val) => {
                  let newVal = val === true ? "TRUE" : val;
                  newVal = newVal === false ? "FALSE" : newVal;
                  propertyType === "main"
                    ? dispatch(
                        editPropertyPvaCell(
                          {
                            propertyId: propertyData.id,
                            propertyValueId: row.id,
                            propertyValueAttributeId: id,
                          },
                          newVal,
                          propertyData
                        )
                      )
                    : dispatch(
                        editSubPropertyPvaCell({
                          parentId: parentId,
                          propertyId: propertyData.id,
                          propertyValueAttributeId: id,
                          propertyValueId: row.id,
                          newValue: newVal,
                          property: propertyData,
                        })
                      );
                }}
                value={value}
                id={`editCell${id}`}
              />
            ),
            propertyValueRowId: row.id,
          };
        }),
        id: row.id,
      };
    }),
  };
};
